import React from "react";
import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/lib/seo";

export default function Terms() {
  usePageMeta({
    title: "Terms of Service — MonetizeGPS",
    description: "Terms of service for MonetizeGPS — the monetization analysis tool for any website.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen grain" data-testid="terms-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
          ▸ Legal
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl font-black tracking-tighter leading-none">
          Terms & Notice
        </h1>
        <p className="mt-6 text-[#333] leading-relaxed">
          Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.
        </p>

        <article className="mt-10 space-y-8 prose-style">
          <Section title="What MonetizeGPS does">
            MonetizeGPS only looks at publicly available information — the same content any visitor sees when they open the website in their browser. It does not access content behind logins, paywalls, or authentication.
          </Section>

          <Section title="Scraping policy">
            MonetizeGPS&apos;s backend respects <code>robots.txt</code> directives and identifies itself with the <code>MonetizeGPSBot</code> User-Agent. We rate-limit requests per domain and refuse to analyze URLs that return 401, 403, or 451 status codes.
            <br /><br />
            MonetizeGPS will refuse to analyze a fixed list of platforms whose Terms of Service explicitly prohibit automated access, including X (Twitter), LinkedIn, Reddit, Facebook, Instagram, TikTok, and Pinterest.
          </Section>

          <Section title="Your responsibility as a user">
            By using MonetizeGPS, you confirm that you have the legal right to analyze the URLs you submit. You are responsible for ensuring that analyzing a particular site does not violate that site&apos;s Terms of Service, applicable laws, or any contractual obligations you may have.
          </Section>

          <Section title="Refunds & cancellation">
            <strong>All sales are final — no refunds.</strong> Because every purchase delivers a MonetizeGPS Blueprint (a digital analysis report) immediately upon payment, all payments are non-refundable. This applies to both the one-time Single Blueprint ($29) and to monthly plans.
            <br /><br />
            <strong>Monthly plans can be cancelled at any time.</strong> Cancellation takes effect at the end of the current 30-day billing period. You keep full access until then, and you will not be charged again. To cancel, sign in and visit your account settings, or email support@monetizegps.com.
            <br /><br />
            The only exception is a duplicate charge or a technical error in delivery of the service (for example, if you were charged but never received your Blueprint due to a system failure). In that case, contact support@monetizegps.com within 7 days and we&apos;ll issue a refund.
          </Section>

          <Section title="Consent to analyze">
            By submitting a URL to MonetizeGPS you represent and warrant that (a) you have the legal right to have that URL analyzed, and (b) you are not bound by any non-disclosure agreement (NDA), confidentiality obligation, or other contractual term that would prohibit having a third party analyze the content at that URL. MonetizeGPS is not liable for URLs you submit in breach of such obligations.
          </Section>

          <Section title="No advice; no warranty">
            MonetizeGPS&apos;s recommendations are informational and generated by automated analysis. They are not financial, legal, tax, or business advice. MonetizeGPS makes no warranty that any recommendation will produce revenue. You should validate every recommendation against your own knowledge of your business.
          </Section>

          <Section title="Data we store">
            MonetizeGPS stores the analyses it produces (the Blueprint) along with the URL you analyzed. If you sign in with Google, we associate Blueprints with your account. We do not sell user data. We do not store the full HTML of pages you analyze. We may use stored analyses and feedback (like thumbs up/down ratings) to improve MonetizeGPS&apos;s recommendations over time.
          </Section>

          <Section title="Email">
            If you ask MonetizeGPS to email a report, we send a single transactional email via our email delivery provider to the address you provide and log the send for delivery debugging. We do not add you to any marketing list.
          </Section>

          <Section title="Limitation of liability">
            MonetizeGPS and its operators are not liable for losses or damages arising from your use of the service, including but not limited to: business decisions made based on MonetizeGPS Blueprints, third-party claims related to URLs you analyze, or downtime / errors in the service.
          </Section>

          <Section title="Contact">
            Issues, abuse reports, or takedown requests can be sent to the email associated with the Resend sender domain. We will respond promptly.
          </Section>
        </article>

        <div className="mt-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-widest hover:bg-[#FF331F]"
            data-testid="back-home"
          >
            ← Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif-display text-2xl md:text-3xl font-bold tracking-tight leading-none mb-2">
        {title}
      </h2>
      <p className="text-[#333] leading-relaxed">{children}</p>
    </section>
  );
}
